/* @flow */
import safeAccess from "safe-access";
import formatServerErrors from "nexia_mobile/formatServerErrors";
import type { ServerError } from "nexia_mobile/formatServerErrors";
import type {
  BackstageResponse,
  SuccessfulBackstageResponse,
  Url,
} from "nexia_mobile/api/types";
import { getLanguageSet } from "nx-components/Locales";

const localeSet = getLanguageSet();
const language = localeSet.nexia_mobile.utils.backstage;

const TIMEOUT_MESSAGE = language.requestTimeout;

// TODO: Should we vendor node_modules/iflow-jquery/index.js.flow so that we can
// export its JQueryXHR type from it?
type JQueryXHR = Object;

// TODO: Make BackstageOptions and Backstage take a type parameter so that we
// can thread in the OptionsType to SuccessfulBackstageResponse.
export type BackstageOptions = {
  success?: ?(SuccessfulBackstageResponse<any>, JQueryXHR) => void,
  pollingUpdate?: ?(Object, JQueryXHR) => void,
  error?: ?(Array<ServerError>, JQueryXHR, ?Object) => void,
  maxRequestsReached?: ?() => void,
  maxRequestsCount?: ?number,
};

export default class Backstage {
  _url: string;
  _onSuccess: $PropertyType<BackstageOptions, "success">;
  _onUpdate: $PropertyType<BackstageOptions, "pollingUpdate">;
  _onError: $PropertyType<BackstageOptions, "error">;
  _onMaxRequestsReached: $PropertyType<BackstageOptions, "maxRequestsReached">;
  _maxRequestsCount: ?number;
  _pollingInterval: IntervalID;
  _requestsCount: number;

  constructor(url: Url, options: BackstageOptions = {}) {
    this._url = url;
    this._onSuccess = options.success;
    this._onUpdate = options.pollingUpdate;
    this._onError = options.error;
    this._maxRequestsCount = options.maxRequestsCount;
    this._onMaxRequestsReached = options.maxRequestsReached;
    this._requestsCount = 0;
  }

  startPolling() {
    this._pollingInterval = setInterval(() => {
      // Our jQuery types are wrong; they don't think that ajaxOptions satisfies
      // the necessary options, and they don't think that jqXHRs have a 'done'
      // property on them.
      // $FlowFixMe
      return $.ajax({
        url: this._url,
        type: "GET",
        dataType: "text",
      })
        .done((jsonData, textStatus, jqXHR) => {
          let data: BackstageResponse;
          try {
            data = JSON.parse(jsonData);
          } catch (error) {
            return;
          }
          if (!data) {
            return;
          }

          if (data.status === "success") {
            this.stopPolling();
            if (typeof this._onSuccess === "function") {
              this._onSuccess(data, jqXHR);
            }
          } else if (data.status === "update") {
            if (typeof this._onUpdate === "function") {
              this._onUpdate(data.options, jqXHR);
            }
          } else if (data.status === "error") {
            this.stopPolling();
            let message = safeAccess(data, "options.errors");
            if (!message) {
              message = safeAccess(data, "options.message");
            }
            if (!message && safeAccess(data, "options.reason") === "timeout") {
              message = TIMEOUT_MESSAGE;
            }
            message = formatServerErrors(message, data);
            const options = safeAccess(data, "options");

            if (typeof this._onError === "function") {
              this._onError(message, jqXHR, options);
            }
          }
        })
        .always((jsonData, textStatus, jqXHR) => {
          if (jqXHR && jqXHR.statusText === "abort") {
            this.stopPolling();
            return;
          }
          this._requestsCount++;

          if (
            this._maxRequestsCount &&
            this._requestsCount > this._maxRequestsCount
          ) {
            if (typeof this._onMaxRequestsReached === "function") {
              this._onMaxRequestsReached();
            }
            if (typeof this._onError === "function") {
              this._onError([{ errorMessage: TIMEOUT_MESSAGE }], jqXHR);
            }
            this.stopPolling();
          }
        });
    }, 1000);
  }

  stopPolling() {
    clearInterval(this._pollingInterval);
  }
}



// WEBPACK FOOTER //
// ./src/backstage.js